/**
 * Traditional Chinese (Taiwan) translation of Travo's privacy policy and terms of use in ./en.ts.
 * The English text governs; if this translation differs from it, the English version prevails.
 */
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/shared/site';
import type { LegalCopy } from './types';

const EMAIL = SUPPORT_EMAIL;
const CLOUDFLARE_PRIVACY = 'https://www.cloudflare.com/privacypolicy/';
const GOOGLE_PRIVACY = 'https://policies.google.com/privacy?hl=zh-TW';
const CONTACT = '[你可以如何就本聲明與我們聯絡？](#contact)';

export const legalZhTW: LegalCopy = {
  eyebrow: '法律條款',
  onThisPage: '本頁內容',
  inShort: '簡而言之：',
  translationNote: '本頁為英文版的翻譯，僅供參考。具有效力的是英文版；中英文版本如有歧異，以英文版為準。',
  readInEnglish: '閱讀英文版',

  privacy: {
    title: '隱私權政策',
    updated: '最後更新：2026 年 10 月 25 日',
    seo: {
      title: '隱私權政策 | Travo',
      description:
        'Travo 如何收集、使用、分享與保護你的資訊，包括用於自動記錄旅程的位置資料，以及你享有的隱私權利。',
      keywords: 'Travo 隱私權政策, 位置資料, 旅行紀錄隱私, 資料刪除, 個人資料保護, GDPR, CCPA'
    },
    sections: [
      {
        id: 'intro',
        heading: '關於本聲明',
        blocks: [
          `本隱私權聲明由以 Travo Labs 名義營運的 ${LEGAL_NAME}（下稱「**我們**」）發布，說明你使用我們的服務（下稱「**服務**」）時，我們可能如何及為何收集、儲存、使用及／或分享（下稱「**處理**」）你的資訊，例如當你：`,
          [
            '下載並使用我們的行動應用程式（Travo），或我們任何其他連結至本隱私權聲明的應用程式',
            '以其他相關方式與我們互動，包括任何銷售、行銷或活動'
          ],
          `**有疑問或疑慮嗎？** 閱讀本隱私權聲明，有助於你了解自己的隱私權利與選擇。如果你不同意我們的政策與做法，請勿使用我們的服務。如果你仍有任何疑問或疑慮，請透過 ${EMAIL} 與我們聯絡。`
        ]
      },
      {
        id: 'summary',
        heading: '重點摘要',
        blocks: [
          '本摘要列出我們隱私權聲明的重點。你可以點選每項重點後方的連結，或使用目錄找到你要查看的章節，以進一步了解各主題的詳細內容。',
          '**我們處理哪些個人資訊？** 當你造訪、使用或瀏覽我們的服務時，我們可能依你與我們及服務互動的方式、你所做的選擇，以及你使用的產品與功能，處理個人資訊。進一步了解[你向我們揭露的個人資訊](#collect)。',
          '**我們是否處理任何敏感個人資訊？** 我們處理位置資料，以提供核心的旅程記錄功能。進一步了解[我們如何處理你的資訊](#process)。',
          '**我們是否從第三方取得任何資訊？** 我們可能從你選擇連結的第三方服務取得資訊，例如用於身分驗證的社群媒體平台。進一步了解[從其他來源收集的資訊](#collect)。',
          '**我們如何處理你的資訊？** 我們處理你的資訊，以提供、改善及管理我們的服務、自動記錄你的旅行紀錄、分析你的旅行模式、產生 AI 推薦，以及與你溝通。我們只在具有正當法律理由時處理你的資訊。進一步了解[我們如何處理你的資訊](#process)。',
          '**我們在哪些情況下、與哪些對象分享個人資訊？** 我們可能在特定情況下與特定第三方分享資訊，包括位置服務供應商、雲端儲存供應商、AI 處理服務，以及為 App 提供廣告的 Google AdMob。進一步了解[我們在何時、與誰分享你的個人資訊](#sharing)。',
          '**我們如何保護你的資訊安全？** 我們已建立組織性與技術性的流程及程序，以保護你的個人資訊。然而，任何透過網際網路進行的電子傳輸或資訊儲存技術，都無法保證 100% 安全，因此我們無法承諾或保證駭客、網路犯罪者或其他未經授權的第三方不會突破我們的安全防護，不當收集、存取、竊取或修改你的資訊。進一步了解[我們如何保護你的資訊安全](#security)。',
          '**你有哪些權利？** 依你所在的地理位置，適用的隱私法律可能使你對自己的個人資訊享有特定權利。進一步了解[你的隱私權利](#rights)。',
          `**你如何行使你的權利？** 行使權利最簡單的方式，是透過 ${EMAIL} 與我們聯絡。我們會依適用的資料保護法律審酌並處理任何請求。`
        ]
      },
      {
        id: 'collect',
        heading: '1. 我們收集哪些資訊？',
        blocks: [
          { h3: '你向我們揭露的個人資訊' },
          { inShort: '我們收集你提供給我們的個人資訊。' },
          '當你在服務上註冊、表示有興趣取得關於我們或我們產品與服務的資訊、參與服務上的活動，或以其他方式與我們聯絡時，我們會收集你自願提供給我們的個人資訊。',
          '**你提供的個人資訊。** 我們收集的個人資訊，取決於你與我們及服務互動的情境、你所做的選擇，以及你使用的產品與功能。我們收集的個人資訊可能包括：',
          ['姓名', '電子郵件地址', '電話號碼', '使用者名稱', '密碼', '個人檔案照片', '聯絡或身分驗證資料'],
          '**敏感資訊。** 在提供服務所必要時，我們會處理下列敏感資訊：',
          [
            '**位置資料：** 當你啟用背景位置追蹤時，我們會持續收集精確的地理位置資料，以自動記錄你造訪的城市與國家',
            '**照片：** 你選擇上傳、用以建立旅行回憶的圖片'
          ],
          '**社群媒體登入資料。** 我們可能讓你選擇使用既有的社群媒體帳號資料（例如你的 Google、Apple 或其他社群媒體帳號）向我們註冊。如果你選擇以這種方式註冊，我們會收集下方「[我們如何處理你的社群帳號登入？](#social-logins)」一節所述的資訊。',
          '你提供給我們的所有個人資訊必須真實、完整且正確，且該等個人資訊如有任何變更，你必須通知我們。',
          { h3: '自動收集的資訊' },
          { inShort: '當你造訪我們的服務時，部分資訊——例如你的網際網路協定（IP）位址及／或瀏覽器與裝置特性——會被自動收集。' },
          '當你造訪、使用或瀏覽服務時，我們會自動收集特定資訊。這些資訊不會揭露你的具體身分（例如你的姓名或聯絡資訊），但可能包括裝置與使用資訊，例如你的 IP 位址、瀏覽器與裝置特性、作業系統、語言偏好、參照網址、裝置名稱、國家、位置、關於你如何及何時使用我們服務的資訊，以及其他技術資訊。這些資訊主要用於維護我們服務的安全與運作，以及供我們內部分析與報告之用。',
          '我們收集的資訊包括：',
          [
            '**位置資料。** 我們同時收集精確與概略的位置資訊。精確位置資料使用 GPS 及其他技術收集地理位置資料，讓我們得知你目前的確切位置；概略位置資料則提供你的大致位置。我們收集多少資訊，取決於你用來存取服務的裝置類型與設定。你可以拒絕授予存取權限，或在裝置上停用「定位」設定，以選擇不讓我們收集這些資訊。然而，由於位置追蹤是 Travo 的核心功能，如果你選擇退出，可能無法使用服務的某些部分。',
            '**使用者 ID 與裝置 ID。** 我們收集唯一識別碼，包括使用者 ID（與你的身分連結，並用於追蹤目的）及裝置 ID，以實現產品個人化、App 功能及分析。',
            '**使用資料。** 我們收集你與產品互動的資訊及其他使用資料，以了解你如何使用我們的服務，使我們能夠進行分析、產品個人化，並改善 App 功能。',
            '**診斷資料。** 我們收集當機資料、效能資料及其他診斷資訊，以分析並改善我們服務的穩定性與效能。這些資料僅用於分析目的。',
            '**日誌與使用資料。** 日誌與使用資料是你存取或使用我們的服務時，我們的伺服器自動收集並記錄於日誌檔中的服務相關、診斷、使用及效能資訊。依你與我們互動的方式，這些日誌資料可能包括你的 IP 位址、裝置資訊、瀏覽器類型與設定、你在服務中的活動資訊（例如與你的使用相關的日期／時間戳記、檢視的頁面與檔案、搜尋，以及你採取的其他動作，例如你使用了哪些功能），以及裝置事件資訊（例如系統活動、錯誤報告（有時稱為「當機傾印」），以及硬體設定）。',
            '**裝置資料。** 我們收集裝置資料，例如你用來存取服務的手機、平板或其他裝置的資訊。依所使用的裝置，這些裝置資料可能包括你的 IP 位址（或代理伺服器）、裝置與應用程式識別碼、位置、瀏覽器類型、硬體型號、網際網路服務供應商及／或行動電信業者、作業系統，以及系統組態資訊。'
          ],
          { h3: '從其他來源收集的資訊' },
          { inShort: '我們可能從你連結至我們 App 的第三方服務收集有限的資料。' },
          '我們可能從其他來源取得關於你的資訊，例如：',
          [
            '**地圖與位置服務**（例如 Mapbox、OpenStreetMap），用以將 GPS 座標轉換為城市與國家名稱',
            '**社群媒體平台**，當你選擇使用社群登入進行身分驗證時',
            '**雲端儲存服務**，你的旅行資料與照片安全儲存於該處'
          ]
        ]
      },
      {
        id: 'process',
        heading: '2. 我們如何處理你的資訊？',
        blocks: [
          { inShort: '我們處理你的資訊，以提供、改善及管理我們的服務、與你溝通、維護安全與防範詐欺，以及遵守法律。經你同意，我們也可能為其他目的處理你的資訊。' },
          '**依你與我們服務互動的方式，我們會基於各種理由處理你的個人資訊，包括：**',
          [
            '**協助建立帳號與身分驗證，以及其他使用者帳號管理事項。** 我們可能處理你的資訊，讓你能建立並登入帳號，並維持帳號正常運作。',
            '**自動記錄你的旅行紀錄。** 我們在背景處理你的位置資料，以自動記錄你造訪的城市與國家、計算你在每個地點停留的天數，並建立完整的旅程時間軸。',
            '**分析你的旅行模式。** 我們處理你的旅行紀錄資料，以提供統計資訊，例如造訪的國家總數、探索過的城市、旅行頻率，以及其他關於你旅行行為的洞察。',
            '**產生 AI 推薦。** 我們可能處理你的旅行紀錄與模式，依你的旅行風格與偏好提供個人化的目的地推薦。',
            '**建立及管理旅行回憶。** 我們處理你上傳的照片及相關位置資料，協助你建立有條理、視覺化的旅行紀錄。',
            '**提供地圖視覺化。** 我們處理你的位置資料，以在互動式地圖上顯示你造訪過的地點，並呈現國家邊界。',
            '**向你傳送管理資訊。** 我們可能處理你的資訊，以向你傳送關於我們產品與服務的詳細資訊、我們條款與政策的變更，以及其他類似資訊。',
            '**徵求意見回饋。** 我們可能在必要時處理你的資訊，以徵求意見回饋，並就你使用我們服務的情形與你聯絡。',
            '**用於目標式廣告與行銷。** 我們可能依你的偏好及適用法律，為開發者的廣告或行銷、產品個人化及第三方廣告目的，處理你的資訊（包括姓名、電子郵件地址、使用者 ID）。這些資料與你的身分連結，並可能用於追蹤目的。',
            '**向你傳送行銷與促銷訊息。** 如果符合你的行銷偏好，我們可能為我們的行銷目的，處理你傳送給我們的個人資訊。你可以隨時選擇不再接收我們的行銷電子郵件。詳情請參閱下方「[你有哪些隱私權利？](#rights)」一節。',
            '**保護我們的服務。** 我們可能處理你的資訊，作為維護我們服務安全的措施之一，包括詐欺監控與防範。',
            '**辨識使用趨勢。** 我們可能處理關於你如何使用我們服務的資訊，以更了解服務的使用情形，進而加以改善。',
            '**拯救或保護個人的重大利益。** 我們可能在必要時處理你的資訊，以拯救或保護個人的重大利益，例如防止傷害。'
          ]
        ]
      },
      {
        id: 'legal-bases',
        heading: '3. 我們依據哪些法律依據處理你的資訊？',
        blocks: [
          { inShort: '我們只在認為有必要，且依適用法律具有正當法律理由（即法律依據）時處理你的個人資訊，例如經你同意、為遵守法律、為提供你服務以締結或履行我們的契約義務、為保護你的權利，或為實現我們的正當商業利益。' },
          { h3: '如果你位於歐盟或英國，本節適用於你。' },
          '《一般資料保護規則》（GDPR）與英國《一般資料保護規則》（UK GDPR）要求我們說明我們處理你的個人資訊所依據的正當法律依據。因此，我們可能依據下列法律依據處理你的個人資訊：',
          [
            '**同意。** 如果你已授權（即同意）我們為特定目的使用你的個人資訊，我們可能處理你的資訊。你可以隨時撤回同意。請參閱下方進一步了解[如何撤回同意](#rights)。',
            '**履行契約。** 當我們認為有必要履行我們對你的契約義務時，包括提供我們的服務，或在與你締結契約前應你的要求，我們可能處理你的個人資訊。',
            '**正當利益。** 當我們認為處理你的資訊對實現我們的正當商業利益而言合理必要，且該等利益未超越你的利益及基本權利與自由時，我們可能處理你的資訊。例如，我們可能為下列所述的部分目的處理你的個人資訊：分析我們服務的使用情形，以便加以改善，吸引並留住使用者；診斷問題及／或防範詐欺活動；以及了解使用者如何使用我們的產品與服務，以改善使用者體驗。',
            '**法律義務。** 當我們認為有必要遵守我們的法律義務時，我們可能處理你的資訊，例如與執法機關或主管機關合作、行使或捍衛我們的法律權利，或在我們涉入的訴訟中揭露你的資訊作為證據。',
            '**重大利益。** 當我們認為有必要保護你或第三人的重大利益時，我們可能處理你的資訊，例如涉及對任何人安全的潛在威脅的情況。'
          ],
          { h3: '如果你位於加拿大，本節適用於你。' },
          '如果你已明確授權（即明示同意）我們為特定目的使用你的個人資訊，或在可推定你已授權的情況下（即默示同意），我們可能處理你的資訊。你可以隨時[撤回同意](#rights)。',
          '在某些例外情況下，依適用法律，我們可能得以在未經你同意的情況下處理你的資訊，例如：',
          [
            '收集顯然符合個人利益，且無法及時取得同意',
            '為進行調查及偵測與防範詐欺',
            '為商業交易，且符合特定條件',
            '資訊包含於證人陳述中，且收集係為評估、處理或理賠保險申請所必要',
            '為辨識受傷、患病或已故人士，並與其近親聯絡',
            '我們有合理理由相信某人曾經、正在或可能成為金融剝削的受害者',
            '可合理預期在取得同意後才收集及使用，將損及資訊的可得性或正確性，且收集係為調查違反協議或違反加拿大或其某一省法律之目的而屬合理',
            '揭露係為遵守與提出紀錄相關的傳票、令狀、法院命令或法院規則所必要',
            '資訊由個人在其受僱、營業或執業過程中產生，且收集符合該資訊產生時的目的',
            '收集僅用於新聞、藝術或文學目的',
            '資訊為公開可得，且屬法規所指定者'
          ]
        ]
      },
      {
        id: 'sharing',
        heading: '4. 我們在何時、與誰分享你的個人資訊？',
        blocks: [
          { inShort: '我們可能在本節所述的特定情況下及／或與下列第三方分享資訊。' },
          '**供應商、顧問及其他第三方服務供應商。** 我們可能與為我們或代表我們提供服務，且為執行該工作而需要存取該等資訊的第三方供應商、服務供應商、承包商或代理人（下稱「**第三方**」）分享你的資料。我們與第三方訂有契約，旨在協助保護你的個人資訊。這表示除非我們指示，否則他們不得對你的個人資訊進行任何處理。他們也不會與我們以外的任何組織分享你的個人資訊。他們亦承諾保護代我們持有的資料，並依我們指示的期間保存。我們可能與之分享個人資訊的第三方如下：',
          [
            '**雲端運算與儲存服務：** Google Firebase 與 Google Cloud（身分驗證、資料庫、推播通知及當機回報）',
            '**位置與地圖服務：** Mapbox（用於城市／地點搜尋及地圖視覺化）；OpenStreetMap Nominatim（用於反向地理編碼）；Google Maps API（用於地圖顯示及國家邊界）',
            '**AI 與機器學習服務：** Google Gemini；Anthropic Claude（用於產生推薦）',
            '**使用者帳號註冊與身分驗證：** Google Sign-In；Apple Sign-In',
            '**分析服務：** Google Analytics for Firebase 與 Mixpanel（用於行動 App）；Cloudflare Web Analytics（用於本網站）',
            '**廣告服務：** Google AdMob（在 Travo App 中顯示並衡量廣告）'
          ],
          '我們也可能需要在下列情況下分享你的個人資訊：',
          [
            '**業務移轉。** 在任何合併、出售公司資產、融資，或我們全部或部分業務被另一家公司收購時或其協商期間，我們可能分享或移轉你的資訊。',
            '**關係企業。** 我們可能與我們的關係企業分享你的資訊，在此情況下，我們會要求該等關係企業遵守本隱私權聲明。關係企業包括我們的母公司，以及任何子公司、合資夥伴，或由我們控制或與我們受共同控制的其他公司。'
          ]
        ]
      },
      {
        id: 'cookies',
        heading: '5. 我們是否使用 Cookie 及其他追蹤技術？',
        blocks: [
          { inShort: '我們自己不設定任何 Cookie。本網站所使用的服務可能會收到你的 IP 位址；如果你播放影片，影片播放器可能會設定 Cookie。Travo App 會顯示 Google AdMob 廣告，並在你同意的範圍內使用裝置與廣告資料。' },
          'www.travolabs.com 由 Cloudflare 託管，Cloudflare 會處理 IP 位址以傳送並保護本網站。造訪次數以不設定 Cookie 的 Cloudflare Web Analytics 統計。網站字型由 Google Fonts 載入，因此載入頁面時 Google 會收到你的 IP 位址。',
          '我們自己不設定任何 Cookie。若你在 Travo 頁面播放示範影片，影片會從 YouTube 載入，YouTube 可能依 Google 的政策設定 Cookie。詳情請參閱 [Cloudflare 隱私權政策](' + CLOUDFLARE_PRIVACY + ')與 [Google 隱私權政策](' + GOOGLE_PRIVACY + ')。',
          '行動應用程式可能使用行動裝置專屬的追蹤技術。Travo App 將「[我們收集哪些資訊？](#collect)」一節所述的識別碼與使用資料，用於分析及當機回報。',
          'Travo App 也會顯示 Google AdMob 的廣告。為了顯示與衡量廣告、防範詐欺，Google 可能會收到你的 IP 位址、裝置與 App 資訊、你與廣告的互動，以及裝置的廣告識別碼（在 iPhone 上，只有在你允許追蹤時才會收到）。',
          '任何廣告載入前，App 會依你所在地的法律要求取得同意：在歐洲經濟區、英國與瑞士會先顯示 Google 的同意表單，接著在 iPhone 上顯示 Apple 的「App 追蹤透明度」提示。之後你可以在 App 帳號頁面的「隱私選項」（有提供時）、iOS「設定」→「隱私權與安全性」→「追蹤」，或 Android 裝置的廣告設定中更改選擇。進一步了解[Google 如何使用採用其服務之網站或應用程式的資訊](https://policies.google.com/technologies/partner-sites?hl=zh-TW)。'
        ]
      },
      {
        id: 'social-logins',
        heading: '6. 我們如何處理你的社群帳號登入？',
        blocks: [
          { inShort: '如果你選擇使用社群媒體帳號註冊或登入我們的服務，我們可能會取得關於你的特定資訊。' },
          '我們的服務讓你能使用第三方社群媒體帳號資料（例如你的 Google 或 Apple 登入資訊）註冊及登入。當你選擇這麼做時，我們會從你的社群媒體供應商收到關於你的特定個人檔案資訊。我們收到的個人檔案資訊可能因社群媒體供應商而異，但通常包括你的姓名、電子郵件地址及個人檔案照片，以及你選擇在該社群媒體平台上公開的其他資訊。',
          '我們只會將收到的資訊用於本隱私權聲明所述的目的，或在相關服務上另行向你清楚說明的目的。請注意，我們無法控制、也不對你的第三方社群媒體供應商對你個人資訊的其他使用負責。我們建議你查閱其隱私權聲明，以了解他們如何收集、使用及分享你的個人資訊，以及你如何在其網站與 App 上設定隱私偏好。'
        ]
      },
      {
        id: 'retention',
        heading: '7. 我們保存你的資訊多久？',
        blocks: [
          { inShort: '除法律另有規定外，我們會在實現本隱私權聲明所述目的所必要的期間內保存你的資訊。' },
          '我們只會在本隱私權聲明所列目的所必要的期間內保存你的個人資訊，除非法律要求或允許更長的保存期間（例如稅務、會計或其他法律要求）。本聲明中沒有任何目的需要我們在使用者帳號終止後，保存你的個人資訊超過九十（90）天。',
          '當我們不再有持續的正當商業需求處理你的個人資訊時，我們會刪除或匿名化該等資訊；如果無法這麼做（例如因為你的個人資訊已儲存於備份檔案中），我們會安全地儲存你的個人資訊，並將其與任何進一步的處理隔離，直到可以刪除為止。',
          '**旅行紀錄資料：** 只要你的帳號仍為有效狀態，你的位置追蹤資料、旅行統計及相關回憶就會被保留，以持續為你提供旅程記錄服務。帳號刪除後，這些資料將在 90 天內永久刪除。'
        ]
      },
      {
        id: 'security',
        heading: '8. 我們如何保護你的資訊安全？',
        blocks: [
          { inShort: '我們致力透過一套組織性與技術性的安全措施保護你的個人資訊。' },
          '我們已實施適當且合理的技術性與組織性安全措施，旨在保護我們處理的任何個人資訊的安全。然而，儘管我們採取了保護措施並努力保護你的資訊，任何透過網際網路進行的電子傳輸或資訊儲存技術，都無法保證 100% 安全，因此我們無法承諾或保證駭客、網路犯罪者或其他未經授權的第三方不會突破我們的安全防護，不當收集、存取、竊取或修改你的資訊。雖然我們會盡力保護你的個人資訊，但個人資訊往返我們服務的傳輸，風險由你自行承擔。你應只在安全的環境中存取服務。',
          '**我們實施的安全措施包括：**',
          [
            '傳輸中與靜態資料的加密',
            '安全的身分驗證機制',
            '定期安全評估',
            '依「需要知道」原則限制個人資訊的存取',
            '使用企業級供應商的安全雲端儲存'
          ]
        ]
      },
      {
        id: 'minors',
        heading: '9. 我們是否收集未成年人的資訊？',
        blocks: [
          { inShort: '我們不會在知情的情況下收集未滿 18 歲兒童的資料，或向其進行行銷。' },
          `我們不會在知情的情況下向未滿 18 歲的兒童索取資料或進行行銷。使用服務即表示你聲明你已年滿 18 歲，或你是該未成年人的父母或監護人，並同意該受扶養的未成年人使用服務。如果我們得知已收集未滿 18 歲使用者的個人資訊，我們會停用該帳號，並採取合理措施，從我們的紀錄中迅速刪除該等資料。如果你得知我們可能收集了任何未滿 18 歲兒童的資料，請透過 ${EMAIL} 與我們聯絡。`
        ]
      },
      {
        id: 'rights',
        heading: '10. 你有哪些隱私權利？',
        blocks: [
          { inShort: '在某些地區，例如歐洲經濟區（EEA）、英國（UK）、瑞士及加拿大，你享有能讓你更充分存取及控制自己個人資訊的權利。你可以隨時檢視、變更或終止你的帳號。' },
          `在某些地區（例如歐洲經濟區、英國、瑞士及加拿大），你依適用的資料保護法律享有特定權利。這些權利可能包括：(i) 請求存取並取得你個人資訊副本的權利；(ii) 請求更正或刪除的權利；(iii) 限制處理你個人資訊的權利；(iv) 於適用時，資料可攜權；以及 (v) 不受自動化決策約束的權利。在特定情況下，你也可能有權反對處理你的個人資訊。你可以使用下方「${CONTACT}」一節提供的聯絡方式與我們聯絡，提出此類請求。`,
          '我們會依適用的資料保護法律審酌並處理任何請求。',
          '如果你位於歐洲經濟區或英國，且認為我們非法處理你的個人資訊，你也有權向你的[會員國資料保護主管機關](https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm)或[英國資料保護主管機關](https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/)提出申訴。',
          '如果你位於瑞士，你可以聯絡[聯邦資料保護與資訊委員](https://www.edoeb.admin.ch/edoeb/en/home.html)。',
          `**撤回同意：** 如果我們是依據你的同意處理你的個人資訊（依適用法律，可能為明示及／或默示同意），你有權隨時撤回同意。你可以隨時使用下方「${CONTACT}」一節提供的聯絡方式與我們聯絡，或在裝置設定或 App 內停用位置追蹤，以撤回同意。`,
          '但請注意，撤回同意不會影響撤回前處理的合法性；在適用法律允許時，也不會影響依據同意以外的合法處理依據所進行的個人資訊處理。',
          `**選擇不接收行銷與促銷訊息：** 你可以隨時點選我們寄送的電子郵件中的取消訂閱連結，或使用下方「${CONTACT}」一節提供的聯絡方式與我們聯絡，以取消訂閱我們的行銷與促銷訊息。之後你將從行銷名單中移除。然而，我們仍可能與你聯繫——例如傳送管理及使用你的帳號所必要的服務相關訊息、回應服務請求，或用於其他非行銷目的。`,
          { h3: '帳號資訊' },
          '如果你想隨時檢視或變更帳號中的資訊，或終止你的帳號，你可以：',
          ['在 Travo 行動應用程式中進入帳號設定', '使用所提供的聯絡資訊與我們聯絡'],
          '在你請求終止帳號後，我們會從我們的使用中資料庫停用或刪除你的帳號與資訊。然而，我們可能在檔案中保留部分資訊，以防範詐欺、排除問題、協助任何調查、執行我們的法律條款及／或遵守適用的法律要求。',
          '**位置資料：** 你可以隨時透過裝置設定或在 App 內停用位置追蹤。然而，停用位置追蹤將使自動記錄旅程的核心功能無法運作。',
          `如果你對自己的隱私權利有任何疑問或意見，可以寫信至 ${EMAIL} 與我們聯絡。`
        ]
      },
      {
        id: 'dnt',
        heading: '11. 「請勿追蹤」功能的控制',
        blocks: [
          '大多數網頁瀏覽器，以及部分行動作業系統與行動應用程式，都具備「請勿追蹤」（Do-Not-Track，「DNT」）功能或設定，你可以啟用此功能，表達你不希望你的線上瀏覽活動資料被監控及收集的隱私偏好。目前尚未有任何辨識及實施 DNT 訊號的統一技術標準定案。因此，我們目前不會回應 DNT 瀏覽器訊號，或任何自動傳達你不希望在線上被追蹤之選擇的其他機制。如果未來採行了我們必須遵循的線上追蹤標準，我們會在本隱私權聲明的修訂版本中告知你該做法。'
        ]
      },
      {
        id: 'us-residents',
        heading: '12. 美國居民是否享有特定的隱私權利？',
        blocks: [
          { inShort: '如果你是加州、科羅拉多州、康乃狄克州、猶他州或維吉尼亞州的居民，你在存取個人資訊方面享有特定權利。' },
          { h3: '我們收集哪些類別的個人資訊？' },
          '在過去十二（12）個月內，我們收集了下列類別的個人資訊：',
          {
            table: {
              head: ['類別', '範例', '是否收集'],
              rows: [
                ['A. 識別碼', '聯絡資料，例如真實姓名、別名、電子郵件地址、帳號名稱及唯一識別碼', '是'],
                ['B. 加州《客戶紀錄法》所定義的個人資訊', '姓名、聯絡資訊', '是'],
                ['C. 州法或聯邦法所保護的分類特徵', '性別與出生日期', '否'],
                ['D. 商業資訊', '交易資訊、購買紀錄、財務資料及付款資訊', '否'],
                ['E. 生物特徵資訊', '指紋與聲紋', '否'],
                ['F. 網際網路或其他類似的網路活動', '瀏覽紀錄、搜尋紀錄、線上行為、興趣資料，以及與我們應用程式的互動', '是'],
                ['G. 地理位置資料', '啟用背景追蹤時持續追蹤的精確裝置位置', '是'],
                ['H. 音訊、電子、視覺、熱感、嗅覺或類似資訊', '使用者為建立旅行回憶而上傳的照片', '是'],
                ['I. 專業或就業相關資訊', '商務聯絡資料', '否'],
                ['J. 教育資訊', '學生紀錄與名錄資訊', '否'],
                ['K. 從所收集的個人資訊推導出的推論', '從所收集的資訊推導出推論，以建立關於偏好、特徵及旅行模式的個人檔案，用於 AI 推薦', '是'],
                ['L. 敏感個人資訊', '精確地理位置資料', '是']
              ]
            }
          },
          '我們會在提供服務所需的範圍內，或依下列期間，使用並保存所收集的個人資訊：',
          [
            'A 類：使用者在我們這裡擁有帳號期間',
            'B 類：使用者在我們這裡擁有帳號期間',
            'F 類：使用者在我們這裡擁有帳號期間',
            'G 類：使用者在我們這裡擁有帳號期間',
            'H 類：使用者在我們這裡擁有帳號期間',
            'K 類：使用者在我們這裡擁有帳號期間',
            'L 類：使用者在我們這裡擁有帳號期間'
          ],
          '在你親自、透過線上、電話或郵件與我們互動的下列情境中，我們也可能收集上述類別以外的其他個人資訊：',
          [
            '透過我們的客戶支援管道接受協助；',
            '參與客戶問卷調查或競賽；以及',
            '協助提供我們的服務及回應你的詢問。'
          ],
          { h3: '我們如何使用及分享你的個人資訊？' },
          '請參閱「[我們如何處理你的資訊？](#process)」一節，了解我們如何使用你的個人資訊。',
          { h3: '你的資訊是否會與其他任何人分享？' },
          '我們可能依據我們與各服務供應商之間的書面契約，向我們的服務供應商揭露你的個人資訊。請參閱「[我們在何時、與誰分享你的個人資訊？](#sharing)」一節，進一步了解我們如何揭露個人資訊。',
          '我們可能將你的個人資訊用於我們自己的商業目的，例如為技術開發與展示進行內部研究。這不視為「出售」你的個人資訊。',
          '在過去十二（12）個月內，我們未曾為商業或營利目的向第三方出售或分享任何個人資訊。在過去十二（12）個月內，我們曾為商業或營利目的向第三方揭露下列類別的個人資訊：',
          [
            'A 類：識別碼',
            'B 類：加州《客戶紀錄法》所定義的個人資訊',
            'G 類：地理位置資料',
            'H 類：音訊、電子、視覺及類似資訊',
            'K 類：從所收集的個人資訊推導出的推論',
            'L 類：敏感個人資訊（地理位置）'
          ],
          '我們為商業或營利目的向其揭露個人資訊的第三方類別，請參閱「[我們在何時、與誰分享你的個人資訊？](#sharing)」一節。',
          { h3: '加州居民' },
          '《加州民法典》（California Civil Code）第 1798.83 條，又稱「陽光法」（Shine The Light），允許身為加州居民的使用者，每年一次免費向我們請求並取得下列資訊：我們在前一個日曆年度內為直接行銷目的向第三方揭露的個人資訊類別（如有），以及與我們分享個人資訊的所有第三方的名稱與地址。如果你是加州居民並希望提出此類請求，請使用下方提供的聯絡資訊，以書面方式向我們提出請求。',
          '如果你未滿 18 歲、居住於加州，且在服務上擁有已註冊的帳號，你有權請求移除你在服務上公開張貼的不想要的資料。如欲請求移除該等資料，請使用下方提供的聯絡資訊與我們聯絡，並附上與你帳號相關聯的電子郵件地址，以及你居住於加州的聲明。我們會確保該資料不會在服務上公開顯示，但請注意，該資料可能無法從我們所有的系統（例如備份等）中完全或全面移除。',
          { h3: 'CCPA 隱私權聲明' },
          '本節僅適用於加州居民。依《加州消費者隱私法》（CCPA），你享有下列權利。',
          '《加州法規彙編》（California Code of Regulations）將「居民」定義為：',
          [
            '(1) 每一位非因暫時或過境目的而身處加州的個人，以及',
            '(2) 每一位以加州為住所、因暫時或過境目的而身處加州以外的個人'
          ],
          '所有其他個人均定義為「非居民」。',
          '如果「居民」的定義適用於你，我們必須就你的個人資訊遵守特定權利與義務。',
          { h3: '你對自己個人資料享有的權利' },
          '**請求刪除資料的權利——刪除請求**',
          '你可以要求刪除你的個人資訊。如果你要求我們刪除你的個人資訊，我們會尊重你的請求並刪除你的個人資訊，但須受法律規定的特定例外限制，例如（但不限於）其他消費者行使其言論自由權、我們因法律義務而產生的遵循要求，或為防範非法活動所需的任何處理。',
          '**受告知的權利——知悉請求**',
          '依情況而定，你有權知悉：',
          [
            '我們是否收集及使用你的個人資訊；',
            '我們收集的個人資訊類別；',
            '所收集的個人資訊的使用目的；',
            '我們是否向第三方出售或分享個人資訊；',
            '我們為商業目的出售、分享或揭露的個人資訊類別；',
            '我們為商業目的向其出售、分享或揭露個人資訊的第三方類別；',
            '收集、出售或分享個人資訊的商業或營利目的；以及',
            '我們收集的關於你的具體個人資訊。'
          ],
          '依適用法律，我們沒有義務為回應消費者請求而提供或刪除已去識別化的消費者資訊，或為驗證消費者請求而重新識別個人資料。',
          '**行使消費者隱私權利時不受歧視的權利**',
          '如果你行使你的隱私權利，我們不會歧視你。',
          '**限制敏感個人資訊使用與揭露的權利**',
          '如果你是加州居民，你有權將你的敏感個人資訊的使用與揭露，限制在執行一般消費者合理預期的服務或提供其合理預期的商品所必要的範圍內。我們目前僅將敏感個人資訊（精確地理位置資料）用於提供旅程記錄服務的核心目的，而這正是本 App 預期的主要功能。',
          '**驗證程序**',
          '收到你的請求後，我們需要驗證你的身分，以確認你就是我們系統中所存資訊的當事人。這些驗證作業需要我們請你提供資訊，以便與你先前提供給我們的資訊進行比對。例如，依你提出的請求類型，我們可能請你提供特定資訊，以便將你提供的資訊與我們已存檔的資訊進行比對，或透過你先前提供給我們的聯絡方式（例如電話或電子郵件）與你聯絡。我們也可能視情況使用其他驗證方式。',
          '我們只會將你在請求中提供的個人資訊，用於驗證你的身分或你提出請求的權限。我們會盡可能避免為驗證目的向你索取額外資訊。然而，如果我們無法從我們已持有的資訊驗證你的身分，我們可能請你提供額外資訊，以驗證你的身分，並用於安全或防範詐欺目的。我們會在完成驗證後，立即刪除你額外提供的資訊。',
          '**其他隱私權利**',
          [
            '你可以反對處理你的個人資訊。',
            '如果你的個人資料不正確或不再相關，你可以請求更正，或要求限制該資訊的處理。',
            '你可以指定授權代理人依 CCPA 代表你提出請求。若授權代理人未提出依 CCPA 已獲有效授權代表你行事的證明，我們可能拒絕其請求。',
            '你可以請求選擇退出未來向第三方出售或分享你的個人資訊。收到選擇退出的請求後，我們會盡快處理該請求，但不會晚於請求提出之日起十五（15）天。'
          ],
          `如欲行使這些權利，你可以寫信至 ${EMAIL} 與我們聯絡，或參閱本文件最下方的聯絡資訊。如果你對我們處理你資料的方式有任何申訴，我們很樂意聽取你的意見。`
        ]
      },
      {
        id: 'updates',
        heading: '13. 我們是否會更新本聲明？',
        blocks: [
          { inShort: '是的，我們會在必要時更新本聲明，以持續遵守相關法律。' },
          '我們可能不時更新本隱私權聲明。更新後的版本將以更新後的「最後更新」日期標示，並自可供存取時起生效。如果我們對本隱私權聲明做出重大變更，我們可能以顯著方式公告該等變更，或直接傳送通知給你。我們鼓勵你經常查閱本隱私權聲明，以了解我們如何保護你的資訊。'
        ]
      },
      {
        id: 'contact',
        heading: '14. 你可以如何就本聲明與我們聯絡？',
        blocks: [
          `如果你對本聲明有任何疑問或意見，可以寫信至 ${EMAIL} 與我們聯絡。`
        ]
      },
      {
        id: 'review',
        heading: '15. 你可以如何檢視、更新或刪除我們向你收集的資料？',
        blocks: [
          `依你所在國家的適用法律，你可能有權請求存取我們向你收集的個人資訊、變更該資訊或將其刪除。如欲請求檢視、更新或刪除你的個人資訊，請寫信至 ${EMAIL}。`,
          '你也可以直接在 Travo App 中管理你的資料，方式如下：',
          [
            '進入帳號設定更新個人檔案資訊',
            '刪除個別的旅行回憶或照片',
            '在 App 設定或裝置設定中停用位置追蹤',
            '請求完整刪除帳號，你的所有資料將在 90 天內移除'
          ],
          '本隱私權政策旨在透明說明 Travo 如何處理你的旅行資料，同時為你提供自動、智慧的旅程記錄體驗。如果你對我們如何保護你的隱私有任何疑問，歡迎隨時與我們聯絡。'
        ]
      }
    ]
  },

  terms: {
    title: '使用條款',
    updated: '最後更新：2026 年 10 月 25 日',
    seo: {
      title: '使用條款 | Travo',
      description: '使用 Travo 旅行紀錄 App 及其相關服務的法律條款：你的帳號、你的內容、可接受的使用方式，以及我們的責任。',
      keywords: 'Travo 使用條款, 服務條款, 法律條款, Travo App 授權'
    },
    sections: [
      {
        id: 'agreement',
        heading: '同意我們的法律條款',
        blocks: [
          `我們是以 Travo Labs 名義營運的 ${LEGAL_NAME}（下稱「**本公司**」、「**我們**」）。`,
          '我們營運 Travo 行動應用程式，以及任何提及或連結至本法律條款（下稱「**法律條款**」）的其他相關產品與服務（下合稱「**服務**」）。',
          'Travo 是一款智慧旅行紀錄 App，可自動追蹤並整理你的旅行紀錄。本 App 會在背景自動追蹤你的位置（城市與國家）、分析你的旅行資料、以照片建立回憶，並依你的旅行模式提供 AI 目的地推薦。',
          `你可以寫信至 ${EMAIL} 與我們聯絡。`,
          '本法律條款構成你（無論以個人身分或代表某一實體，下稱「**你**」）與 Travo Labs 之間，就你存取及使用服務所訂立具法律拘束力的協議。你同意，存取服務即表示你已閱讀、了解並同意受本法律條款全部內容的拘束。如果你不同意本法律條款的全部內容，你即被明確禁止使用服務，且必須立即停止使用。',
          '可能不時於服務上公布的補充條款與條件或文件，特此以提及方式明確納入本法律條款。我們保留得依我們的單獨裁量，隨時基於任何理由變更或修改本法律條款的權利。我們會透過更新本法律條款的「最後更新」日期，提醒你任何變更，且你放棄就每項此類變更收到個別通知的任何權利。你有責任定期查閱本法律條款，以掌握更新內容。在修訂後的法律條款公布之日後，你繼續使用服務，即表示你受該修訂後法律條款中的變更拘束，並視為已知悉且接受該等變更。',
          '我們建議你列印一份本法律條款留存。'
        ]
      },
      {
        id: 'services',
        heading: '1. 我們的服務',
        blocks: [
          '使用服務時所提供的資訊，並非意圖在任何司法管轄區或國家，向任何個人或實體散布或供其使用，若該等散布或使用將違反法律或法規，或將使我們在該司法管轄區或國家須受任何登記要求的規範。因此，選擇從其他地點存取服務的人，係出於自身的主動決定，並應自行負責遵守當地法律（如果且在當地法律適用的範圍內）。',
          '服務僅供個人、非商業用途，協助你自動追蹤及整理你的旅行紀錄。服務會使用你裝置的定位服務，記錄你造訪的城市與國家。'
        ]
      },
      {
        id: 'ip',
        heading: '2. 智慧財產權',
        blocks: [
          { h3: '我們的智慧財產' },
          '我們是服務中所有智慧財產權的所有人或被授權人，包括服務中的所有原始碼、資料庫、功能、軟體、應用程式設計、音訊、視訊、文字、照片及圖像（下合稱「**內容**」），以及其中所含的商標、服務標章及標誌（下稱「**標章**」）。',
          '我們的內容與標章受美國及世界各地的著作權法與商標法（以及各種其他智慧財產權與不公平競爭法律）及條約保護。',
          '內容與標章係於服務中或透過服務「按現狀」提供，僅供你個人、非商業使用。',
          { h3: '你對我們服務的使用' },
          '在你遵守本法律條款（包括下方「[禁止行為](#prohibited)」一節）的前提下，我們授予你非專屬、不可轉讓、可撤銷的授權，以：',
          ['存取服務；以及', '下載或列印你已合法取得存取權限的任何部分內容的副本。'],
          '以上均僅限於你個人的非商業用途。',
          '除本節或我們法律條款其他部分另有規定外，未經我們事先明確書面許可，不得為任何商業目的複製、重製、彙整、重新發布、上傳、張貼、公開展示、編碼、翻譯、傳輸、散布、出售、授權或以其他方式利用服務的任何部分及任何內容或標章。',
          `如果你希望以本節或我們法律條款其他部分所規定以外的方式使用服務、內容或標章，請將你的請求寄至：${EMAIL}。如果我們曾授予你張貼、重製或公開展示我們服務或內容任何部分的許可，你必須表明我們是服務、內容或標章的所有人或授權人，並確保在張貼、重製或展示我們的內容時，任何著作權或專有權聲明均有出現或清楚可見。`,
          '我們保留在服務、內容及標章之中及之上所有未明確授予你的權利。',
          '任何違反本智慧財產權規定的行為，均構成對我們法律條款的重大違約，你使用我們服務的權利將立即終止。',
          { h3: '你的提交內容與使用者內容' },
          '在使用我們的服務之前，請仔細閱讀本節及「[禁止行為](#prohibited)」一節，以了解當你透過服務張貼或上傳任何內容時，(a) 你授予我們的權利，以及 (b) 你所負的義務。',
          '**提交內容：** 當你直接向我們傳送任何關於服務的問題、評論、建議、想法、意見回饋或其他資訊（下稱「**提交內容**」）時，你同意將該提交內容的所有智慧財產權讓與我們。你同意我們擁有該提交內容，並有權為任何合法目的（無論商業或其他目的）不受限制地使用及傳播，而無須向你表明出處或給予補償。',
          '**你須對你張貼或上傳的內容負責：** 透過服務的任何部分向我們傳送提交內容，即表示你：',
          [
            '確認你已閱讀並同意我們的「[禁止行為](#prohibited)」，且不會透過服務張貼、傳送、發布、上傳或傳輸任何違法、騷擾、仇恨、有害、誹謗、猥褻、霸凌、辱罵、歧視、威脅任何個人或群體、露骨性內容、虛假、不正確、欺騙或誤導的提交內容；',
            '在適用法律允許的範圍內，放棄對任何該等提交內容的所有著作人格權；',
            '保證任何該等提交內容均為你的原創，或你擁有提交該等提交內容的必要權利與授權，且你有完整權限就你的提交內容授予我們上述權利；以及',
            '保證並聲明你的提交內容不構成機密資訊。'
          ],
          '你須對你的提交內容獨自負責，且你明確同意，對於因你違反 (a) 本節、(b) 任何第三方的智慧財產權，或 (c) 適用法律而使我們遭受的任何及所有損失，向我們償付。'
        ]
      },
      {
        id: 'representations',
        heading: '3. 使用者聲明',
        blocks: [
          '使用服務即表示你聲明並保證：(1) 你提交的所有註冊資訊均真實、正確、最新且完整；(2) 你會維持該等資訊的正確性，並在必要時立即更新該等註冊資訊；(3) 你具有法律上的行為能力，且同意遵守本法律條款；(4) 依你居住地的法律，你並非未成年人；(5) 你不會透過自動化或非人為方式存取服務，無論是透過機器人程式、腳本或其他方式；(6) 你不會將服務用於任何非法或未經授權的目的；以及 (7) 你對服務的使用不會違反任何適用的法律或法規。',
          '如果你提供任何不實、不正確、非最新或不完整的資訊，我們有權暫停或終止你的帳號，並拒絕你目前或未來對服務（或其任何部分）的任何及所有使用。'
        ]
      },
      {
        id: 'registration',
        heading: '4. 使用者註冊',
        blocks: [
          '你可能需要註冊才能使用服務。你同意對你的密碼保密，並對你帳號與密碼的所有使用負責。如果我們依單獨裁量認定你選擇的使用者名稱不適當、猥褻或有其他令人反感之處，我們保留移除、收回或變更該使用者名稱的權利。'
        ]
      },
      {
        id: 'prohibited',
        heading: '5. 禁止行為',
        blocks: [
          '你不得為我們提供服務之目的以外的任何目的存取或使用服務。除經我們特別背書或核准者外，服務不得用於任何商業活動。',
          '身為服務的使用者，你同意不會：',
          [
            '未經我們書面許可，有系統地從服務擷取資料或其他內容，以直接或間接建立或彙編任何集合、彙編、資料庫或目錄。',
            '欺騙、詐欺或誤導我們及其他使用者，特別是任何試圖取得使用者密碼等敏感帳號資訊的行為。',
            '規避、停用或以其他方式干擾服務的安全相關功能，包括防止或限制任何內容的使用或複製，或對服務及／或其中所含內容的使用施加限制的功能。',
            '依我們的判斷，貶低、玷污或以其他方式損害我們及／或服務。',
            '使用從服務取得的任何資訊騷擾、辱罵或傷害他人。',
            '不當使用我們的支援服務，或提交不實的濫用或不當行為檢舉。',
            '以不符合任何適用法律或法規的方式使用服務。',
            '未經授權對服務進行框架嵌入或連結。',
            '上傳或傳輸（或試圖上傳或傳輸）病毒、特洛伊木馬或其他資料，包括過度使用大寫字母及濫發訊息（持續張貼重複文字），而干擾任何一方不間斷地使用及享受服務，或修改、損害、中斷、變更或干擾服務的使用、特色、功能、運作或維護。',
            '對系統進行任何自動化使用，例如使用腳本傳送評論或訊息，或使用任何資料探勘、機器人程式或類似的資料蒐集與擷取工具。',
            '從任何內容中刪除著作權或其他專有權聲明。',
            '試圖冒充其他使用者或他人，或使用其他使用者的使用者名稱。',
            '上傳或傳輸（或試圖上傳或傳輸）任何作為被動或主動資訊收集或傳輸機制的資料，包括但不限於透明圖形交換格式（「gif」）、1×1 像素、網路信標、Cookie 或其他類似裝置（有時稱為「間諜軟體」、「被動收集機制」或「pcms」）。',
            '干擾、中斷服務或與服務連線的網路或服務，或對其造成不當負擔。',
            '騷擾、惹惱、恐嚇或威脅我們任何參與向你提供服務任何部分的員工或代理人。',
            '試圖繞過服務中旨在防止或限制存取服務或服務任何部分的任何措施。',
            '複製或改作服務的軟體，包括但不限於 Flash、PHP、HTML、JavaScript 或其他程式碼。',
            '除適用法律允許者外，解譯、反編譯、反組譯或逆向工程構成服務或以任何方式組成服務一部分的任何軟體。',
            '除因標準搜尋引擎或網際網路瀏覽器的使用所致者外，使用、啟動、開發或散布任何存取服務的自動化系統，包括但不限於任何網路爬蟲、機器人程式、作弊工具、資料擷取程式或離線閱讀器，或使用或啟動任何未經授權的腳本或其他軟體。',
            '使用採購代理人或購買代理人在服務上進行購買。',
            '對服務進行任何未經授權的使用，包括以電子或其他方式收集使用者的使用者名稱及／或電子郵件地址，以寄送未經請求的電子郵件，或以自動化方式或假借名義建立使用者帳號。',
            '將服務用於任何與我們競爭的行為，或以其他方式將服務及／或內容用於任何營利活動或商業事業。',
            '試圖操縱、竄改或人為灌水你的旅行統計或位置資料。',
            '與他人分享你的帳號憑證，或允許他人存取你的帳號。'
          ]
        ]
      },
      {
        id: 'contributions',
        heading: '6. 使用者產生的貢獻內容',
        blocks: [
          '服務可能邀請你上傳、張貼或分享內容，包括但不限於照片、旅行回憶、評論及其他資料（下稱「**貢獻內容**」）。你的貢獻內容可能可供服務的其他使用者檢視。當你建立或提供任何貢獻內容時，即表示你聲明並保證：',
          [
            '你的貢獻內容的建立、散布、傳輸、公開展示或演出，以及存取、下載或複製，現在及將來均不會侵害任何第三方的專有權利，包括但不限於著作權、專利權、商標權、營業秘密或著作人格權。',
            '你是你的貢獻內容的創作者及所有人，或擁有必要的授權、權利、同意、免責同意及許可，以使用你的貢獻內容，並授權我們、服務及服務的其他使用者以服務及本法律條款所預期的任何方式使用你的貢獻內容。',
            '對於你的貢獻內容中每一位可辨識的個人，你均已取得其書面同意、免責同意及／或許可，得使用每一位該等可辨識個人的姓名或肖像，以便以服務及本法律條款所預期的任何方式納入及使用你的貢獻內容。',
            '你的貢獻內容並非虛假、不正確或誤導。',
            '你的貢獻內容並非未經請求或未經授權的廣告、促銷資料、老鼠會、連鎖信、垃圾訊息、大量郵件或其他形式的招攬。',
            '你的貢獻內容並非猥褻、淫穢、色情、骯髒、暴力、騷擾、文字誹謗、言詞誹謗或有其他令人反感之處（由我們判定）。',
            '你的貢獻內容不會嘲笑、戲弄、貶低、恐嚇或辱罵任何人。',
            '你的貢獻內容不會被用於騷擾或威脅（依該等用語的法律意義）任何其他人，以及煽動對特定個人或族群的暴力。',
            '你的貢獻內容不違反任何適用的法律、法規或規則。',
            '你的貢獻內容不侵犯任何第三方的隱私權或公開權。',
            '你的貢獻內容不違反任何關於兒童色情的適用法律，或其他旨在保護未成年人健康或福祉的法律。',
            '你的貢獻內容不包含任何與種族、國籍、性別、性傾向或身體障礙有關的冒犯性言論。',
            '你的貢獻內容不會以其他方式違反本法律條款的任何規定或任何適用的法律或法規，也不會連結至違反上述規定的資料。'
          ],
          '任何違反前述規定而使用服務的行為，均違反本法律條款，並可能導致（除其他後果外）你使用服務的權利遭終止或暫停。'
        ]
      },
      {
        id: 'contribution-license',
        heading: '7. 貢獻內容授權',
        blocks: [
          '將你的貢獻內容張貼至服務的任何部分，即表示你自動授予我們（且你聲明並保證你有權授予）一項不受限制、無限、不可撤銷、永久、非專屬、可轉讓、免權利金、已全額付清的全球性權利及授權，得為任何目的（商業、廣告或其他目的）託管、使用、複製、重製、揭露、出售、轉售、發布、廣播、重新命名、封存、儲存、快取、公開演出、公開展示、重新格式化、翻譯、傳輸、摘錄（全部或部分）及散布該等貢獻內容（包括但不限於你的影像與聲音），並得就該等貢獻內容製作衍生著作或將其併入其他著作，以及授予及授權前述權利的再授權。該等使用與散布得以任何媒體格式、透過任何媒體管道進行。',
          '本授權適用於現在已知或日後開發的任何形式、媒體或技術，並包括我們使用你的姓名、公司名稱及加盟名稱（如適用），以及你提供的任何商標、服務標章、商號、標誌及個人與商業形象。你放棄對你的貢獻內容的所有著作人格權，並保證你的貢獻內容未曾以其他方式主張著作人格權。',
          '我們不對你的貢獻內容主張任何所有權。你保有你所有貢獻內容的完整所有權，以及與你的貢獻內容相關的任何智慧財產權或其他專有權利。對於你在服務任何區域提供的貢獻內容中的任何陳述或表示，我們概不負責。你須對你在服務上的貢獻內容獨自負責，且你明確同意免除我們就你的貢獻內容所負的任何及所有責任，並不對我們採取任何與你的貢獻內容相關的法律行動。',
          '我們有權依我們單獨且絕對的裁量：(1) 編輯、刪改或以其他方式變更任何貢獻內容；(2) 重新分類任何貢獻內容，以將其放置於服務上更適當的位置；以及 (3) 隨時基於任何理由預先審查或刪除任何貢獻內容，而無須通知。我們沒有監控你的貢獻內容的義務。'
        ]
      },
      {
        id: 'app-license',
        heading: '8. 行動應用程式授權',
        blocks: [
          { h3: '使用授權' },
          '如果你透過行動應用程式存取服務，我們授予你一項可撤銷、非專屬、不可轉讓的有限權利，得在你擁有或控制的無線電子裝置上安裝及使用該行動應用程式，並嚴格依本法律條款中所含本行動應用程式授權的條款與條件，在該等裝置上存取及使用該行動應用程式。你不得：',
          [
            '除適用法律允許者外，對本應用程式進行反編譯、逆向工程、反組譯、試圖推導其原始碼或解密；',
            '對本應用程式進行任何修改、改作、改良、強化、翻譯或製作衍生著作；',
            '在存取或使用本應用程式時，違反任何適用的法律、規則或法規；',
            '移除、變更或遮蔽我們或本應用程式授權人所張貼的任何專有權聲明（包括任何著作權或商標聲明）；',
            '將本應用程式用於任何營利活動、商業事業，或其非設計或非預定用途的其他目的；',
            '透過網路或其他允許多個裝置或使用者同時存取或使用的環境提供本應用程式；',
            '使用本應用程式建立直接或間接與本應用程式競爭，或以任何方式替代本應用程式的產品、服務或軟體；',
            '使用本應用程式向任何網站傳送自動化查詢，或寄送任何未經請求的商業電子郵件；或',
            '在設計、開發、製造、授權或散布任何搭配本應用程式使用的應用程式、配件或裝置時，使用任何專有資訊，或我們的任何介面或其他智慧財產。'
          ],
          { h3: 'Apple 與 Android 裝置' },
          '當你使用從 App Store 或 Google Play（各稱「**App 經銷商**」）取得的行動應用程式存取服務時，適用下列條款：',
          [
            '我們的行動應用程式授予你的授權，僅限於在使用 Apple iOS 或 Android 作業系統（視情況而定）的裝置上，依適用 App 經銷商服務條款所訂的使用規則使用本應用程式的不可轉讓授權；',
            '我們負責依本法律條款中所含本行動應用程式授權的條款與條件所載，或依適用法律的其他要求，提供與行動應用程式相關的任何維護與支援服務，且你承認各 App 經銷商均無任何義務提供與行動應用程式相關的任何維護與支援服務；',
            '如果行動應用程式不符合任何適用的保證，你可以通知適用的 App 經銷商，該 App 經銷商得依其條款與政策，退還你為行動應用程式支付的購買價款（如有），且在適用法律允許的最大範圍內，該 App 經銷商對行動應用程式不負任何其他保證義務；',
            '你聲明並保證：(i) 你不位於受美國政府禁運的國家，或被美國政府指定為「支持恐怖主義」的國家；且 (ii) 你未被列於任何美國政府禁止或限制往來對象名單；',
            '使用行動應用程式時，你必須遵守適用的第三方協議條款，例如，如果你使用 VoIP 應用程式，則在使用行動應用程式時，你不得違反其無線數據服務協議；以及',
            '你承認並同意，App 經銷商是本法律條款中所含本行動應用程式授權之條款與條件的第三方受益人，且各 App 經銷商均有權（並視為已接受該權利）以第三方受益人的身分，對你執行本法律條款中所含本行動應用程式授權的條款與條件。'
          ]
        ]
      },
      {
        id: 'management',
        heading: '9. 服務管理',
        blocks: [
          '我們保留以下權利，但不負以下義務：(1) 監控服務是否有違反本法律條款的情事；(2) 對依我們單獨裁量認定違反法律或本法律條款的任何人採取適當的法律行動，包括但不限於向執法機關舉報該使用者；(3) 依我們單獨裁量且不受限制地，拒絕、限制存取、限制可用性或停用（在技術可行的範圍內）你的任何貢獻內容或其任何部分；(4) 依我們單獨裁量且不受限制、無須通知亦不負責任地，從服務移除或以其他方式停用所有過大或以任何方式對我們的系統造成負擔的檔案與內容；以及 (5) 以其他旨在保護我們的權利與財產並促進服務正常運作的方式管理服務。'
        ]
      },
      {
        id: 'privacy',
        heading: '10. 隱私權政策',
        blocks: [
          '我們重視資料隱私與安全。請查閱我們的[隱私權政策](/privacy)。使用服務即表示你同意受我們的隱私權政策拘束，該政策已納入本法律條款。請注意，服務託管於美國。如果你從世界上任何其他地區存取服務，而該地區關於個人資料收集、使用或揭露的法律或其他要求與美國適用法律不同，則透過你持續使用服務，你即將你的資料移轉至美國，且你明確同意你的資料移轉至美國並在美國處理。',
          '此外，我們不會在知情的情況下接受、要求或索取兒童的資訊，也不會在知情的情況下向兒童進行行銷。因此，依美國《兒童線上隱私保護法》（COPPA），如果我們實際得知任何未滿 13 歲的人在未取得必要且可驗證的父母同意下向我們提供了個人資訊，我們會在合理可行的範圍內盡快從服務中刪除該資訊。'
        ]
      },
      {
        id: 'termination',
        heading: '11. 期間與終止',
        blocks: [
          '在你使用服務期間，本法律條款持續完全有效。在不限制本法律條款任何其他規定的情況下，我們保留得依我們單獨裁量，在無須通知亦不負責任的情況下，基於任何理由或無理由，拒絕任何人存取及使用服務（包括封鎖特定 IP 位址）的權利，包括但不限於因違反本法律條款所含的任何聲明、保證或承諾，或任何適用的法律或法規。我們得依我們單獨裁量，隨時在不事先警告的情況下，終止你對服務的使用或參與，或刪除你的帳號及你張貼的任何內容或資訊。',
          '如果我們基於任何理由終止或暫停你的帳號，你被禁止以你的名義、虛假或借用的名義，或任何第三方的名義註冊及建立新帳號，即使你可能是代表該第三方行事。除終止或暫停你的帳號外，我們保留採取適當法律行動的權利，包括但不限於尋求民事、刑事及禁制令救濟。'
        ]
      },
      {
        id: 'modifications',
        heading: '12. 修改與中斷',
        blocks: [
          '我們保留得依我們單獨裁量，隨時基於任何理由，在無須通知的情況下變更、修改或移除服務內容的權利。然而，我們沒有更新服務上任何資訊的義務。對於服務的任何修改、價格變更、暫停或終止，我們對你或任何第三方概不負責。',
          '我們無法保證服務隨時可用。我們可能遇到硬體、軟體或其他問題，或需要進行與服務相關的維護，導致中斷、延遲或錯誤。我們保留得隨時基於任何理由，在無須通知你的情況下變更、修訂、更新、暫停、終止或以其他方式修改服務的權利。你同意，對於服務停機或終止期間，因你無法存取或使用服務而造成的任何損失、損害或不便，我們不負任何責任。本法律條款的任何內容均不得解釋為我們有義務維護及支援服務，或提供與其相關的任何修正、更新或版本。'
        ]
      },
      {
        id: 'law',
        heading: '13. 準據法',
        blocks: [
          '本法律條款以中華民國（臺灣）法律為準據法。因本法律條款所生或與其相關的任何爭議，以臺灣臺北地方法院為第一審管轄法院；但你居住國的消費者保護法賦予你在當地提起訴訟之權利者，不在此限。'
        ]
      },
      {
        id: 'disputes',
        heading: '14. 爭議解決',
        blocks: [
          { h3: '非正式協商' },
          '為加速解決並控制由你或我們（各稱「**一方**」，合稱「**雙方**」）提出、與本法律條款相關的任何爭議、爭執或請求（各稱「**爭議**」，合稱「**各項爭議**」）的成本，雙方同意在提起法院訴訟程序前，先就任何爭議（下方明確規定的爭議除外）嘗試進行至少三十（30）天的非正式協商。該非正式協商自一方向另一方發出書面通知時開始。',
          { h3: '非正式協商的例外' },
          '雙方同意，下列爭議不適用上述關於非正式協商的規定：(a) 任何尋求執行或保護任一方的智慧財產權，或涉及其有效性的爭議；(b) 任何與竊盜、盜版、侵犯隱私或未經授權使用之指控相關或因此所生的爭議；以及 (c) 任何禁制令救濟的請求。'
        ]
      },
      {
        id: 'corrections',
        heading: '15. 更正',
        blocks: [
          '服務上的資訊可能含有排版錯誤、不正確或遺漏之處，包括說明、價格、可用性及其他各種資訊。我們保留隨時更正任何錯誤、不正確或遺漏之處，以及變更或更新服務上資訊的權利，而無須事先通知。'
        ]
      },
      {
        id: 'disclaimer',
        heading: '16. 免責聲明',
        blocks: [
          '服務係依「按現狀」及「按可得狀態」提供。你同意你使用服務的風險由你獨自承擔。在法律允許的最大範圍內，我們不就服務及你對服務的使用提供任何明示或默示的保證，包括但不限於適售性、特定目的適用性及未侵權的默示保證。我們不就服務內容，或任何連結至服務的網站或行動應用程式內容的正確性或完整性，提供任何保證或聲明，且對於下列任何事項，我們不承擔任何責任：(1) 內容與資料的任何錯誤、失誤或不正確；(2) 因你存取及使用服務而導致的任何性質的人身傷害或財產損害；(3) 任何未經授權存取或使用我們的安全伺服器及／或其中所儲存的任何及所有個人資訊及／或財務資訊；(4) 往返服務之傳輸的任何中斷或停止；(5) 任何第三方可能透過服務傳輸或傳輸至服務的任何程式錯誤、病毒、特洛伊木馬或類似物；及／或 (6) 任何內容與資料中的任何錯誤或遺漏，或因使用透過服務張貼、傳輸或以其他方式提供的任何內容而產生的任何種類的損失或損害。',
          '對於任何第三方透過服務、任何超連結網站，或任何橫幅或其他廣告中所展示的任何網站或行動應用程式所刊登廣告或提供的任何產品或服務，我們不提供保證、背書或擔保，亦不承擔責任，且我們不會成為你與任何第三方產品或服務供應商之間任何交易的當事人，亦不以任何方式負責監控該等交易。',
          '如同透過任何媒介或在任何環境中購買產品或服務，你應運用你最佳的判斷，並在適當時審慎行事。',
          '**位置資料免責聲明：** 服務仰賴位置資料提供旅程記錄功能。我們不保證位置資料的正確性、完整性或可靠性。位置追蹤可能無法在所有地區或狀況下正常運作。你承認以位置為基礎的功能可能有其固有的限制與不精確之處。'
        ]
      },
      {
        id: 'liability',
        heading: '17. 責任限制',
        blocks: [
          '在任何情況下，我們或我們的董事、員工或代理人，均不對你或任何第三方負擔任何直接、間接、衍生性、懲戒性、附隨性、特殊或懲罰性損害賠償責任，包括因你使用服務而產生的利潤損失、收入損失、資料遺失或其他損害，即使我們已被告知發生該等損害的可能性亦然。',
          '無論本文件中有任何相反規定，我們基於任何原因、不論訴訟形式為何，對你所負的責任，在任何時候均以你在任何訴因發生前六（6）個月期間內向我們支付的金額（如有）為限。',
          '美國某些州的法律及國際法律不允許對默示保證設限，或排除或限制特定損害賠償。如果這些法律適用於你，上述部分或全部免責聲明或限制可能不適用於你，且你可能享有額外的權利。'
        ]
      },
      {
        id: 'indemnification',
        heading: '18. 賠償',
        blocks: [
          '你同意為我們（包括我們的子公司、關係企業，以及我們各自的所有主管、代理人、合夥人及員工）進行抗辯、予以賠償並使其免受損害，使其免於任何第三方因下列事項所生或所致而提出的任何損失、損害、責任、請求或要求，包括合理的律師費用與開支：(1) 你的貢獻內容；(2) 對服務的使用；(3) 違反本法律條款；(4) 任何違反你在本法律條款中所做的聲明與保證；(5) 你侵害第三方的權利，包括但不限於智慧財產權；或 (6) 對你透過服務聯繫的服務其他使用者所做的任何明顯有害行為。',
          '儘管有前述規定，我們保留由你負擔費用、就你須向我們賠償的任何事項取得專屬抗辯與控制權的權利，且你同意由你負擔費用，配合我們就該等請求進行抗辯。我們在得知任何受本賠償條款規範的請求、訴訟或程序時，會盡合理努力通知你。'
        ]
      },
      {
        id: 'user-data',
        heading: '19. 使用者資料',
        blocks: [
          '為管理服務的效能，我們會保存你傳輸至服務的特定資料，以及與你使用服務相關的資料。雖然我們會定期例行備份資料，但你須對你傳輸的所有資料，或與你使用服務所進行的任何活動相關的所有資料獨自負責。',
          '你同意，對於任何該等資料的任何遺失或毀損，我們對你不負任何責任，且你特此放棄因任何該等資料遺失或毀損而對我們提起訴訟的任何權利。',
          '你可以隨時透過服務匯出你的旅行資料。我們建議你定期備份重要的旅行回憶與照片。'
        ]
      },
      {
        id: 'electronic',
        heading: '20. 電子通訊、交易及簽章',
        blocks: [
          '造訪服務、寄送電子郵件給我們及填寫線上表單，均構成電子通訊。你同意接收電子通訊，且你同意我們以電子方式、透過電子郵件及在服務上向你提供的所有協議、通知、揭露及其他通訊，均符合該等通訊須以書面為之的任何法律要求。',
          '你特此同意使用電子簽章、契約、訂單及其他紀錄，並同意以電子方式交付由我們或透過服務發起或完成之交易的通知、政策及紀錄。你特此放棄任何司法管轄區內任何法令、法規、規則、條例或其他法律下，要求原始簽章、交付或保存非電子紀錄，或以電子方式以外的任何方式付款或給予抵免的任何權利或要求。'
        ]
      },
      {
        id: 'misc',
        heading: '21. 其他條款',
        blocks: [
          '本法律條款，以及我們在服務上或就服務所公布的任何政策或營運規則，構成你與我們之間的完整協議與共識。我們未行使或執行本法律條款的任何權利或規定，不構成對該權利或規定的放棄。',
          '本法律條款在法律允許的最大範圍內有效。我們得隨時將我們的任何或全部權利與義務讓與他人。對於超出我們合理控制範圍的任何原因所造成的任何損失、損害、延遲或未能履行，我們不負任何責任。',
          '如果本法律條款的任何規定或規定的任何部分被認定為違法、無效或無法執行，該規定或該部分規定視為可與本法律條款分離，且不影響其餘任何規定的效力與可執行性。',
          '你與我們之間不因本法律條款或服務的使用而建立任何合資、合夥、僱傭或代理關係。你同意，本法律條款不會因係由我們起草而作出對我們不利的解釋。你特此放棄你可能基於本法律條款的電子形式，以及本法律條款當事人未簽署本法律條款而提出的任何及所有抗辯。'
        ]
      },
      {
        id: 'contact',
        heading: '22. 聯絡我們',
        blocks: [
          '如需解決與服務相關的申訴，或取得關於服務使用的進一步資訊，請透過以下方式與我們聯絡：',
          `**以 Travo Labs 名義營運的 ${LEGAL_NAME}** — 電子郵件：${EMAIL}`
        ]
      }
    ]
  }
};
